import type {
  FloatingWindowPosition,
  FloatingWindowLayout,
  DockMode,
  FloatingWindowSize,
} from "../types/floatingWindow";

export const SNAP_THRESHOLD = 80;

export const DOCKED_SIDE_WIDTH = 480;

export const DOCKED_BOTTOM_HEIGHT = 320;

export const DEFAULT_VIEWPORT_WIDTH = 1024;

export const DEFAULT_VIEWPORT_HEIGHT = 768;

export const DEFAULT_HEADER_HEIGHT = 40;

export const MIN_WINDOW_WIDTH = 320;

export const FLOATING_WINDOW_BOUNDS_SELECTOR = ".layout-content";

type FloatingWindowBounds = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export const canUseDOM = () =>
  typeof window !== "undefined" && typeof document !== "undefined";

export const getViewportBounds = (): FloatingWindowBounds => {
  if (!canUseDOM()) {
    return {
      x: 0,
      y: 0,
      width: DEFAULT_VIEWPORT_WIDTH,
      height: DEFAULT_VIEWPORT_HEIGHT,
    };
  }

  return {
    x: 0,
    y: 0,
    width: window.innerWidth,
    height: window.innerHeight,
  };
};

export const getFloatingWindowBoundsElement = () => {
  if (!canUseDOM()) {
    return null;
  }

  return document.querySelector<HTMLElement>(FLOATING_WINDOW_BOUNDS_SELECTOR);
};

export const getFloatingWindowBounds = (): FloatingWindowBounds => {
  const element = getFloatingWindowBoundsElement();

  if (!element) {
    return getViewportBounds();
  }

  const rect = element.getBoundingClientRect();

  if (rect.width <= 0 || rect.height <= 0) {
    return getViewportBounds();
  }

  return {
    x: rect.left,
    y: rect.top,
    width: rect.width,
    height: rect.height,
  };
};

export const getCenteredPosition = (
  width: number,
  height: number,
): FloatingWindowPosition => {
  const bounds = getFloatingWindowBounds();

  return {
    x: bounds.x + Math.max((bounds.width - width) / 2, 0),
    y: bounds.y + Math.max((bounds.height - height) / 2, 0),
  };
};

export const getInitialFloatingLayout = (
  width: number,
  height: number,
): FloatingWindowLayout => ({
  position: getCenteredPosition(width, height),
  size: {
    width,
    height,
  },
});

export const getDockLayout = (
  mode: DockMode,
  currentSize?: FloatingWindowSize,
): FloatingWindowLayout | null => {
  const bounds = getFloatingWindowBounds();

  switch (mode) {
    case "left":
      return {
        position: {
          x: bounds.x,
          y: bounds.y,
        },
        size: {
          width: currentSize?.width ?? DOCKED_SIDE_WIDTH,
          height: bounds.height,
        },
      };

    case "right": {
      const width = currentSize?.width ?? DOCKED_SIDE_WIDTH;

      return {
        position: {
          x: bounds.x + bounds.width - width,
          y: bounds.y,
        },
        size: {
          width,
          height: bounds.height,
        },
      };
    }

    case "bottom": {
      const height = currentSize?.height ?? DOCKED_BOTTOM_HEIGHT;

      return {
        position: {
          x: bounds.x,
          y: bounds.y + bounds.height - height,
        },
        size: {
          width: bounds.width,
          height,
        },
      };
    }

    default:
      return null;
  }
};

export const getSnapDockMode = (
  position: FloatingWindowPosition,
  size: FloatingWindowSize,
): DockMode | null => {
  const bounds = getFloatingWindowBounds();

  const bottomSnap =
    position.y + size.height >= bounds.y + bounds.height - SNAP_THRESHOLD;

  const leftSnap = position.x <= bounds.x + SNAP_THRESHOLD;

  const rightSnap =
    position.x + size.width >= bounds.x + bounds.width - SNAP_THRESHOLD;

  /**
   * Important:
   * bottom has priority over left/right so dragging to the bottom corners
   * does not accidentally dock to a side.
   */
  if (bottomSnap) {
    return "bottom";
  }

  if (leftSnap) {
    return "left";
  }

  if (rightSnap) {
    return "right";
  }

  return null;
};

export const getResizeConfig = (minimized: boolean, dockMode: DockMode) => {
  if (minimized) {
    return false;
  }

  if (dockMode === "bottom") {
    return {
      top: true,
      right: false,
      bottom: false,
      left: false,
      topRight: false,
      bottomRight: false,
      bottomLeft: false,
      topLeft: false,
    };
  }

  if (dockMode === "left") {
    return {
      top: false,
      right: true,
      bottom: false,
      left: false,
      topRight: false,
      bottomRight: false,
      bottomLeft: false,
      topLeft: false,
    };
  }

  if (dockMode === "right") {
    return {
      top: false,
      right: false,
      bottom: false,
      left: true,
      topRight: false,
      bottomRight: false,
      bottomLeft: false,
      topLeft: false,
    };
  }

  return true;
};

export const clampPositionToViewport = (
  position: FloatingWindowPosition,
  size: FloatingWindowSize,
): FloatingWindowPosition => {
  const bounds = getFloatingWindowBounds();

  return {
    x: Math.min(
      Math.max(position.x, bounds.x),
      bounds.x + Math.max(bounds.width - size.width, 0),
    ),
    y: Math.min(
      Math.max(position.y, bounds.y),
      bounds.y + Math.max(bounds.height - size.height, 0),
    ),
  };
};

export const getUndockedLayout = (
  dockMode: DockMode,
  lastFloatingLayout: FloatingWindowLayout,
  defaultWidth: number,
  defaultHeight: number,
): FloatingWindowLayout => {
  if (dockMode !== "bottom") {
    return lastFloatingLayout;
  }

  const bounds = getFloatingWindowBounds();

  return {
    position: {
      x: getCenteredPosition(defaultWidth, defaultHeight).x,
      y: bounds.y + Math.max(bounds.height - defaultHeight - 80, 0),
    },
    size: {
      width: defaultWidth,
      height: defaultHeight,
    },
  };
};

export const getDockedResizeLayout = (
  dockMode: DockMode,
  nextSize: FloatingWindowSize,
): FloatingWindowLayout | null => {
  if (dockMode === "bottom") {
    return getDockLayout("bottom", nextSize);
  }

  if (dockMode === "left" || dockMode === "right") {
    const bounds = getFloatingWindowBounds();

    return getDockLayout(dockMode, {
      width: nextSize.width,
      height: bounds.height,
    });
  }

  return null;
};
